import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { contextService, documentService, ApiError } from '@/services'
import { useUpdatesStream } from '@/hooks/useUpdatesStream'
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback'
import { useAppStore } from '@/stores/appStore'
import { useFillGapsSession } from '@/hooks/useFillGapsSession'
import { UploadZone } from '@/components/overview/UploadZone'
import { GapAccordion } from '@/components/fill-gaps/GapAccordion'
import { GapRow } from '@/components/fill-gaps/GapRow'
import { GapChat } from '@/components/fill-gaps/GapChat'
import { GapPerGapUpload } from '@/components/fill-gaps/GapPerGapUpload'
import { GapPreFillCards } from '@/components/fill-gaps/GapPreFillCards'
import { GapStatusCounts } from '@/components/fill-gaps/GapStatusCounts'
import { GapResolvedToast } from '@/components/fill-gaps/GapResolvedToast'
import { ResolvedGapsSection } from '@/components/fill-gaps/ResolvedGapsSection'
import {
  activeGapCount,
  partitionGaps,
} from '@/components/fill-gaps/partitionGaps'
import type {
  ContextGap,
  GapStatusCounts as Counts,
} from '@/types/api'

/** Backend max page size; the list endpoint defaults to 20 and truncates. */
const GAP_FETCH_LIMIT = 100

/**
 * Fill Gaps — the unified input surface. One screen replaces the legacy
 * Fill / Confirm / Tell-Us triplet. Page owns:
 *  - one gap fetch, split client-side into active rows + resolved history
 *  - the controlled accordion expansion (deep-linkable via `?gapId=`)
 *  - global vs per-gap upload routing
 *  - per-gap interview sessions via `useFillGapsSession` (lock on expand)
 *  - SSE refetch on `gap` / `context` events
 *
 * Status overrides from `gapUpdates` win over the fetched list until the
 * next refetch — a resolved row leaves the active list immediately and
 * shows up under "Resolved" once the refetch confirms it.
 */
export function FillGapsPage() {
  const startupId = useAppStore((s) => s.startup?.id ?? null)
  const setOpenGapCount = useAppStore((s) => s.setOpenGapCount)
  const [searchParams, setSearchParams] = useSearchParams()
  const initialGapParam = searchParams.get('gapId')

  const [gaps, setGaps] = useState<ContextGap[]>([])
  const [counts, setCounts] = useState<Counts | null>(null)
  // True once the first fetch settles. Later refetches (SSE, uploads) keep
  // the current view up instead of flashing the empty state.
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadingGapId, setUploadingGapId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string[]>([])

  const fetchGaps = useCallback(async () => {
    if (!startupId) return
    setError(null)
    try {
      // Unfiltered on purpose: the backend computes statusCounts after the
      // status filter, and resolved rows are listed below the accordion.
      const result = await contextService.getGaps(startupId, {
        limit: GAP_FETCH_LIMIT,
      })
      const list = result.gaps ?? []
      setGaps(list)
      setCounts(result.statusCounts ?? null)
      setOpenGapCount(activeGapCount(result.statusCounts, list))
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return
      setError(err instanceof Error ? err.message : 'Failed to load gaps.')
    } finally {
      setHasLoaded(true)
    }
  }, [startupId, setOpenGapCount])

  useEffect(() => {
    void fetchGaps()
  }, [fetchGaps])

  const debouncedRefetch = useDebouncedCallback(() => {
    void fetchGaps()
  }, 250)
  useUpdatesStream({
    startupId,
    filters: { entityTypes: ['gap', 'context'] },
    enabled: !!startupId,
    onUpdate: debouncedRefetch,
  })

  const session = useFillGapsSession({
    startupId,
    gaps,
    expandedGapIds: expanded,
  })

  const { active: activeGaps, resolved: resolvedGaps } = useMemo(
    () => partitionGaps(gaps, session.statusOverrides),
    [gaps, session.statusOverrides]
  )

  // Auto-expand the gap referenced by `?gapId=` when the gap list arrives,
  // then drop the param so refresh doesn't keep snapping back.
  useEffect(() => {
    if (!initialGapParam) return
    if (activeGaps.some((g) => g.id === initialGapParam)) {
      setExpanded((prev) =>
        prev.includes(initialGapParam) ? prev : [...prev, initialGapParam]
      )
      setSearchParams(
        (params) => {
          params.delete('gapId')
          return params
        },
        { replace: true }
      )
    }
  }, [initialGapParam, activeGaps, setSearchParams])

  // Collapse rows that left the active list — resolved by a reply (so the
  // toast isn't competing with an open body) or by a refetch. Collapsing
  // is what gives the row's session back.
  useEffect(() => {
    const activeIds = new Set(activeGaps.map((g) => g.id))
    setExpanded((prev) => {
      const next = prev.filter((id) => activeIds.has(id))
      return next.length === prev.length ? prev : next
    })
  }, [activeGaps])

  const handleGlobalUpload = useCallback(
    async (files: File[]) => {
      if (!startupId) return
      setUploadError(null)
      try {
        await documentService.upload(startupId, files)
        void fetchGaps()
      } catch (err) {
        setUploadError(
          err instanceof Error ? err.message : 'Upload failed. Try again.'
        )
      }
    },
    [startupId, fetchGaps]
  )

  const { applyStatusUpdates, sendMessage } = session
  const handlePerGapUpload = useCallback(
    async (gapId: string, files: File[]) => {
      if (!startupId) return
      setUploadError(null)
      setUploadingGapId(gapId)
      try {
        await documentService.uploadForGap(startupId, gapId, files)
        // We optimistically nudge status — the next gap refetch corrects.
        applyStatusUpdates({ [gapId]: 'in_progress' })
        void fetchGaps()
      } catch (err) {
        setUploadError(
          err instanceof Error ? err.message : 'Upload failed. Try again.'
        )
      } finally {
        setUploadingGapId(null)
      }
    },
    [startupId, fetchGaps, applyStatusUpdates]
  )

  const handlePreFillConfirm = useCallback(
    (gapId: string, field: string, value: string) => {
      const message = `Confirming ${humanize(field)}: ${value}`
      void sendMessage(gapId, message)
    },
    [sendMessage]
  )

  const allCaughtUp = hasLoaded && !error && activeGaps.length === 0

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <Header counts={counts} />

      <div className="mt-6">
        <UploadZone
          onUpload={handleGlobalUpload}
          onRejected={setUploadError}
          compact={activeGaps.length > 0}
        />
      </div>

      {uploadError && <ErrorBanner text={uploadError} tone="error" />}
      {error && <ErrorBanner text={error} tone="warning" />}
      {session.sessionError && (
        <div
          role="alert"
          className="mt-3 text-sm px-3 py-2 rounded-md border border-warning/30 bg-warning/10 text-text-primary flex items-center justify-between gap-3"
        >
          <span>{session.sessionError}</span>
          <button
            type="button"
            onClick={session.retrySession}
            className="shrink-0 min-h-8 px-3 rounded-md font-medium text-primary hover:bg-bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Retry
          </button>
        </div>
      )}

      {allCaughtUp ? (
        <AllCaughtUp />
      ) : (
        <div className="mt-6">
          <GapAccordion value={expanded} onValueChange={setExpanded}>
            {activeGaps.map((gap) => (
              <GapRow key={gap.id} gap={gap} resolved={false}>
                <GapPreFillCards gap={gap} onConfirm={handlePreFillConfirm} />
                <GapChat
                  suggestedQuestion={gap.suggestedQuestion ?? null}
                  messages={session.messagesByGap[gap.id] ?? []}
                  sending={session.busyByGap[gap.id] ?? false}
                  resolved={false}
                  onSend={(content) => {
                    void sendMessage(gap.id, content)
                  }}
                />
                <GapPerGapUpload
                  gapId={gap.id}
                  targetLabel={gap.domain ?? 'this gap'}
                  busy={uploadingGapId === gap.id}
                  onUpload={(id, files) => {
                    void handlePerGapUpload(id, files)
                  }}
                  onRejected={setUploadError}
                />
              </GapRow>
            ))}
          </GapAccordion>
        </div>
      )}

      <ResolvedGapsSection gaps={resolvedGaps} />

      <GapResolvedToast
        resolvedGapTitle={session.recentlyResolved?.title ?? null}
        onDismiss={session.clearRecentlyResolved}
      />
    </div>
  )
}

interface HeaderProps {
  counts: Counts | null
}

function Header({ counts }: HeaderProps) {
  return (
    <header>
      <h1 className="text-2xl font-display font-semibold text-text-primary tracking-tight">
        Fill Gaps
      </h1>
      <p className="mt-1 text-sm text-text-secondary">
        Answer a question, drop a doc, or confirm a suggestion. Each gap
        you close deepens your context engine.
      </p>
      <GapStatusCounts counts={counts} />
    </header>
  )
}

function AllCaughtUp() {
  return (
    <div className="mt-8 rounded-lg border border-border-default bg-bg-primary px-6 py-10 text-center">
      <h2 className="text-base font-display font-semibold text-text-primary">
        All caught up.
      </h2>
      <p className="mt-1 text-sm text-text-secondary">
        No open gaps right now. Upload a doc and we’ll surface anything
        we still need.
      </p>
    </div>
  )
}

function ErrorBanner({
  text,
  tone,
}: {
  text: string
  tone: 'error' | 'warning'
}) {
  return (
    <div
      role="alert"
      className={
        tone === 'error'
          ? 'mt-3 text-sm px-3 py-2 rounded-md border border-error/30 bg-error/10 text-text-primary'
          : 'mt-3 text-sm px-3 py-2 rounded-md border border-warning/30 bg-warning/10 text-text-primary'
      }
    >
      {text}
    </div>
  )
}

function humanize(field: string): string {
  return field
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
}
